<template>
  <div class="home general">
    <h1 class="section-title">Lo más popular</h1>

    <div class="filters">
      <va-input v-model="since" type="date" label="Desde" />
      <va-input v-model="until" type="date" label="Hasta" />
      <va-input v-model="top" type="number" label="Top" />
      <va-button color="info" :disabled="isLoading" @click="loadReport">
        Consultar
      </va-button>
    </div>

    <p v-if="error" class="analytics-error">{{ error }}</p>
    <p v-if="isLoading" class="analytics-status">Cargando ranking...</p>

    <div v-if="report" class="summary">
      <div class="summary-item">
        <span>Reproducciones</span>
        <strong>{{ report.summary.plays }}</strong>
      </div>
      <div class="summary-item">
        <span>Canciones</span>
        <strong>{{ report.summary.unique_songs }}</strong>
      </div>
      <div class="summary-item">
        <span>Artistas</span>
        <strong>{{ report.summary.unique_artists }}</strong>
      </div>
      <div class="summary-item">
        <span>Géneros</span>
        <strong>{{ report.summary.unique_genres }}</strong>
      </div>
      <div class="summary-item">
        <span>Usuarios</span>
        <strong>{{ report.summary.unique_users }}</strong>
      </div>
    </div>

    <h2 class="section-title">Canciones</h2>
    <va-data-table
      class="ekitable"
      :items="report ? report.songs : []"
      :columns="songColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(song_title)="{ rowData }">
        <router-link
          v-if="rowData.songId"
          class="table-span linked"
          :to="{ name: 'songDetail', params: { id: rowData.songId } }"
        >
          {{ rowData.song_title }}
        </router-link>
        <span v-else>{{ rowData.song_title }}</span>
      </template>
      <template #cell(avg_score)="{ rowData }">
        {{ formatNumber(rowData.avg_score) }}
      </template>
    </va-data-table>

    <h2 class="section-title">Artistas</h2>
    <va-data-table
      class="ekitable"
      :items="report ? report.artists : []"
      :columns="artistColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(avg_score)="{ rowData }">
        {{ formatNumber(rowData.avg_score) }}
      </template>
    </va-data-table>

    <h2 class="section-title">Géneros</h2>
    <va-data-table
      class="ekitable"
      :items="report ? report.genres : []"
      :columns="genreColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(avg_score)="{ rowData }">
        {{ formatNumber(rowData.avg_score) }}
      </template>
    </va-data-table>

    <h2 class="section-title">Usuarios</h2>
    <va-data-table
      class="ekitable"
      :items="report ? report.users : []"
      :columns="userColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(avg_score)="{ rowData }">
        {{ formatNumber(rowData.avg_score) }}
      </template>
    </va-data-table>
  </div>
</template>

<script>
import requester from "@/services/requester";

function currentMonthRange() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const lastDay = String(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()).padStart(2, "0");
  return {
    since: `${now.getFullYear()}-${month}-01`,
    until: `${now.getFullYear()}-${month}-${lastDay}`,
  };
}

export default {
  name: "PopularPlayback",
  data() {
    const month = currentMonthRange();
    return {
      top: 15,
      since: month.since,
      until: month.until,
      isLoading: false,
      error: "",
      report: null,
      songColumns: [
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "song_title", sortable: false, label: "Canción" },
        { key: "artist_name", sortable: false, label: "Artista" },
        { key: "genre_name", sortable: false, label: "Género" },
        { key: "unique_users", sortable: false, label: "Usuarios" },
        { key: "avg_score", sortable: false, label: "Puntaje medio" },
      ],
      artistColumns: [
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "artist_name", sortable: false, label: "Artista" },
        { key: "unique_songs", sortable: false, label: "Canciones" },
        { key: "unique_users", sortable: false, label: "Usuarios" },
        { key: "avg_score", sortable: false, label: "Puntaje medio" },
      ],
      genreColumns: [
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "genre_name", sortable: false, label: "Género" },
        { key: "unique_songs", sortable: false, label: "Canciones" },
        { key: "unique_users", sortable: false, label: "Usuarios" },
        { key: "avg_score", sortable: false, label: "Puntaje medio" },
      ],
      userColumns: [
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "user_name", sortable: false, label: "Usuario" },
        { key: "unique_songs", sortable: false, label: "Canciones" },
        { key: "unique_artists", sortable: false, label: "Artistas" },
        { key: "unique_genres", sortable: false, label: "Géneros" },
        { key: "avg_score", sortable: false, label: "Puntaje medio" },
      ],
    };
  },
  methods: {
    formatNumber(value) {
      if (value == null || value === "") {
        return "-";
      }
      return Number(value).toFixed(1);
    },
    async loadReport() {
      this.isLoading = true;
      this.error = "";
      try {
        const query = [`top=${this.top || 15}`];
        if (this.since) {
          query.push(`since=${this.since}`);
        }
        if (this.until) {
          query.push(`until=${this.until}`);
        }
        const data = await requester.Get(`/descriptive?${query.join("&")}`, true);
        if (data.status === 200 && data.data) {
          this.report = data.data;
        } else {
          this.error = "No se pudo cargar el ranking.";
        }
      } catch (e) {
        console.log(e);
        this.error = "No se pudo cargar el ranking.";
      }
      this.isLoading = false;
    },
  },
  created() {
    this.loadReport();
  },
};
</script>

<style lang="scss" scoped>
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  padding: 0 15px 20px;
}

.summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 15px 20px;
}

.summary-item {
  background: #123d62;
  border-radius: 8px;
  padding: 12px 16px;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.analytics-error,
.analytics-status {
  padding: 0 15px 16px;
}

.analytics-error {
  color: #f8b4b4;
}
</style>
